import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { FaBoxes, FaDollyFlatbed } from 'react-icons/fa';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import BreadCrumb from '~/components/BreadCrumb';
import ConfirmExitDialog from '~/components/Dialogs/ConfirmExit';
import ConfirmProcedureDialog from '~/components/Dialogs/ConfirmProcedure';
import DefaultInput from '~/components/Inputs/Default';
import ExitButton from '~/components/Buttons/Exit';
import SaveButton from '~/components/Buttons/Save';
import Toggle from '~/components/Toggle';

import { useToast } from '~/hooks/toast';

import api from '~/services/api';

import { Container, Wrapper } from './styles';

interface IFormMaterial {
  name: string;
  erp_code: string;
  active: boolean;
}

interface ILocation {
  id: string;
  name: string;
  erp_code: string;
  active: boolean;
}

const schema = Yup.object().shape({
  name: Yup.string().required('Campo obrigatório'),
});

const New: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { state } = useLocation<ILocation>();
  const [isOpenConfirmProcedure, setIsOpenConfirmProcedure] = useState(false);
  const [isOpenExit, setIsOpenExit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const { handleSubmit, register, control, errors } = useForm<IFormMaterial>({
    context: {
      erpCodeEnabled: state === undefined,
    },
    resolver: yupResolver(schema),
    defaultValues: {
      active: state?.active,
    },
  });

  const submitForm = useCallback(
    async (data: IFormMaterial) => {
      try {
        setIsSaving(true);

        if (state === undefined) {
          await api.post('/items/materials', data);

          addToast({
            type: 'success',
            title: 'Cadastro realizado!',
            description: 'Material cadastrado com sucesso.',
          });
        } else {
          await api.put(`/items/materials`, { id: state.id, ...data });

          addToast({
            type: 'success',
            title: 'Cadastro atualizado!',
            description: 'Material atualizado com sucesso.',
          });
        }

        history.push('/items/materials');
      } catch (error: any) {
        setIsSaving(false);

        addToast({
          type: 'error',
          title: 'Ocorreu um erro!',
          description: error.response.data.message,
        });
      }
    },
    [addToast, history, state],
  );

  const onExit = () => {
    history.push('/items/materials');
  };

  return (
    <Container>
      <BreadCrumb
        main="Itens"
        path="/items/main"
        firstChildPath="/items/materials"
        firstChild="Materiais"
        secondChild={
          state === undefined ? 'Novo Material' : 'Edição de Material'
        }
        mainIcon={FaBoxes}
        firstChildIcon={FaDollyFlatbed}
        secondChildIcon={FaDollyFlatbed}
        mainColored
        firstChildColored
      />

      <form onSubmit={handleSubmit(submitForm)}>
        <div
          style={{ maxWidth: 700, display: 'flex', flexWrap: 'wrap', gap: 10 }}
        >
          <Wrapper style={{ width: 180 }}>
            <DefaultInput
              name="erp_code"
              labelFor="erp_code"
              labelText="Código ERP"
              type="text"
              defaultValue={state?.erp_code}
              error={errors?.erp_code?.message}
              ref={register}
              maxLength={10}
              readOnly={
                state?.erp_code !== null &&
                state?.erp_code !== undefined &&
                state?.erp_code !== ''
              }
            />
          </Wrapper>

          <Wrapper style={{ width: 350 }}>
            <DefaultInput
              name="name"
              labelFor="name"
              labelText="Nome"
              type="text"
              defaultValue={state?.name}
              error={errors?.name?.message}
              ref={register}
              maxLength={60}
            />
          </Wrapper>

          <Wrapper style={{ width: 100 }}>
            <Controller
              render={props => (
                <Toggle
                  onChange={e => props.onChange(e)}
                  checked={props.value}
                />
              )}
              name="active"
              defaultValue
              control={control}
            />
          </Wrapper>
        </div>

        <div
          style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end' }}
        >
          <SaveButton
            type="button"
            processing={isSaving}
            disabled={isSaving}
            onClick={() => {
              setIsOpenConfirmProcedure(true);
            }}
          />

          <ExitButton
            style={{ marginLeft: 10 }}
            type="button"
            disabled={isSaving}
            onClick={() => setIsOpenExit(true)}
          />
        </div>
      </form>

      <ConfirmProcedureDialog
        isOpen={isOpenConfirmProcedure}
        setIsOpen={setIsOpenConfirmProcedure}
        onConfirm={() => handleSubmit(submitForm)()}
        title="Confirma gravação do material?"
        subTitle=""
        opConfirm="Sim"
        type=""
      />

      <ConfirmExitDialog
        isOpen={isOpenExit}
        setIsOpen={setIsOpenExit}
        onExit={onExit}
      />
    </Container>
  );
};

export default New;
